import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Receipt, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search, 
  Filter,
  Eye,
  FileText,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Activity,
  Calculator,
  Package
} from 'lucide-react';
import { invoiceRecords, accountsPayableSummary, paymentProcessingDashboard } from '../../data/invoiceData';

const InvoiceVerification = ({ onBack, onSelectInvoice, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const renderWorkflowBanner = () => {
    if (navigationSource === 'purchase-workflow') {
      return (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-orange-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-orange-800">來自業務流程: 採購週期管理</h3>
                <p className="text-sm text-orange-600">發票核對與付款處理階段</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回採購管理</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const getVerificationStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'verified': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'scheduled': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
      case 'paid':
      case 'verified': return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'pending':
      case 'scheduled': return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredInvoices = invoiceRecords.filter(invoice => {
    const matchesSearch = invoice.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.supplier_invoice_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.verification_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">發票核對管理</h1>
          <p className="text-gray-600 mt-1">管理供應商發票核對和付款處理</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>發票清單</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">待核對發票</p>
              <p className="text-2xl font-bold text-gray-900">
                {invoiceRecords.filter(i => i.verification_status === 'pending').length}
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1" />
            <span className="text-sm text-yellow-600">需要處理</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">應付總額</p>
              <p className="text-2xl font-bold text-gray-900">
                ${accountsPayableSummary.current_period.total_outstanding.toLocaleString()}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-blue-500 mr-1" />
            <span className="text-sm text-blue-600">當期餘額</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">逾期金額</p>
              <p className="text-2xl font-bold text-gray-900">
                ${accountsPayableSummary.current_period.overdue_amount.toLocaleString()}
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
            <span className="text-sm text-red-600">需要關注</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">本月付款</p>
              <p className="text-2xl font-bold text-gray-900">
                ${accountsPayableSummary.current_period.paid_this_month.toLocaleString()}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">已完成付款</span>
          </div>
        </div>
      </div>

      {/* Recent Invoices */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">最近發票記錄</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  發票編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  供應商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  金額
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  到期日
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  核對狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  付款狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoiceRecords.slice(0, 5).map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invoice.invoice_number}</div>
                    <div className="text-sm text-gray-500">{invoice.supplier_invoice_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{invoice.supplier_name}</div>
                    <div className="text-sm text-gray-500">PO: {invoice.po_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">${invoice.total_amount.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">{invoice.currency}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(invoice.due_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getVerificationStatusColor(invoice.verification_status)}`}>
                      {getStatusIcon(invoice.verification_status)}
                      <span>
                        {invoice.verification_status === 'approved' && '已核准'}
                        {invoice.verification_status === 'rejected' && '已拒絕'}
                        {invoice.verification_status === 'pending' && '待核對'}
                        {invoice.verification_status === 'verified' && '已核對'}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(invoice.payment_status)}`}>
                      {getStatusIcon(invoice.payment_status)}
                      <span>
                        {invoice.payment_status === 'paid' && '已付款'}
                        {invoice.payment_status === 'cancelled' && '已取消'}
                        {invoice.payment_status === 'pending' && '待付款'}
                        {invoice.payment_status === 'scheduled' && '已排程'}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice.id);
                        setActiveView('detail');
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      查看詳情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Accounts Payable Aging */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">應付帳款帳齡分析</h3>
          <div className="space-y-4">
            {accountsPayableSummary.aging_analysis.map((aging, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{aging.period}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${aging.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    ${aging.amount.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">供應商應付款</h3>
          <div className="space-y-4">
            {accountsPayableSummary.supplier_breakdown.slice(0, 5).map((supplier, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{supplier.supplier_name}</div>
                  <div className="text-xs text-gray-500">
                    {supplier.next_payment_date ? `到期: ${supplier.next_payment_date}` : '無待付款項'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    ${supplier.outstanding_amount.toLocaleString()}
                  </div>
                  {supplier.overdue_amount > 0 && (
                    <div className="text-xs text-red-600">
                      逾期: ${supplier.overdue_amount.toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderInvoiceList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">發票清單</h1>
          <p className="text-gray-600 mt-1">所有供應商發票記錄</p>
        </div>
        <button
          onClick={() => setActiveView('dashboard')}
          className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>返回儀表板</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜尋發票編號或供應商..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">所有狀態</option>
                <option value="pending">待核對</option>
                <option value="verified">已核對</option>
                <option value="approved">已核准</option>
                <option value="rejected">已拒絕</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  發票資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  供應商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  金額資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  日期資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  核對狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  付款狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invoice.invoice_number}</div>
                    <div className="text-sm text-gray-500">供應商發票: {invoice.supplier_invoice_number}</div>
                    <div className="text-sm text-gray-500">PO: {invoice.po_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{invoice.supplier_name}</div>
                    <div className="text-sm text-gray-500">付款條件: {invoice.payment_terms}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">小計: ${invoice.subtotal.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">稅額: ${invoice.tax_amount.toLocaleString()}</div>
                    <div className="text-sm font-medium text-gray-900">總計: ${invoice.total_amount.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">發票日期: {new Date(invoice.invoice_date).toLocaleDateString()}</div>
                    <div className="text-sm text-gray-500">收到日期: {new Date(invoice.received_date).toLocaleDateString()}</div>
                    <div className="text-sm text-gray-500">到期日期: {new Date(invoice.due_date).toLocaleDateString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getVerificationStatusColor(invoice.verification_status)}`}>
                      {getStatusIcon(invoice.verification_status)}
                      <span>
                        {invoice.verification_status === 'approved' && '已核准'}
                        {invoice.verification_status === 'rejected' && '已拒絕'}
                        {invoice.verification_status === 'pending' && '待核對'}
                        {invoice.verification_status === 'verified' && '已核對'}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(invoice.payment_status)}`}>
                      {getStatusIcon(invoice.payment_status)}
                      <span>
                        {invoice.payment_status === 'paid' && '已付款'}
                        {invoice.payment_status === 'cancelled' && '已取消'}
                        {invoice.payment_status === 'pending' && '待付款'}
                        {invoice.payment_status === 'scheduled' && '已排程'}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice.id);
                        setActiveView('detail');
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      查看詳情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderInvoiceDetail = () => {
    const invoice = invoiceRecords.find(i => i.id === selectedInvoice);
    if (!invoice) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">發票詳情</h1>
            <p className="text-gray-600 mt-1">{invoice.invoice_number}</p>
          </div>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回清單</span>
          </button>
        </div>

        {/* Invoice Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">發票資訊</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">發票編號:</span>
                <span className="text-gray-900 font-medium">{invoice.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">供應商發票號:</span>
                <span className="text-gray-900 font-medium">{invoice.supplier_invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">採購訂單:</span>
                <span className="text-gray-900 font-medium">{invoice.po_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">供應商:</span>
                <span className="text-gray-900 font-medium">{invoice.supplier_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">發票日期:</span>
                <span className="text-gray-900 font-medium">
                  {new Date(invoice.invoice_date).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">收到日期:</span>
                <span className="text-gray-900 font-medium">
                  {new Date(invoice.received_date).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">到期日期:</span>
                <span className="text-gray-900 font-medium">
                  {new Date(invoice.due_date).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">付款條件:</span>
                <span className="text-gray-900 font-medium">{invoice.payment_terms}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">金額資訊</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">小計:</span>
                <span className="text-gray-900 font-medium">${invoice.subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">稅額:</span>
                <span className="text-gray-900 font-medium">${invoice.tax_amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-900 font-semibold">總計:</span>
                <span className="text-gray-900 font-semibold text-lg">${invoice.total_amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">幣別:</span>
                <span className="text-gray-900 font-medium">{invoice.currency}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">核對狀態:</span>
                <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getVerificationStatusColor(invoice.verification_status)}`}>
                  {getStatusIcon(invoice.verification_status)}
                  <span>
                    {invoice.verification_status === 'approved' && '已核准'}
                    {invoice.verification_status === 'rejected' && '已拒絕'}
                    {invoice.verification_status === 'pending' && '待核對'}
                    {invoice.verification_status === 'verified' && '已核對'}
                  </span>
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">付款狀態:</span>
                <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(invoice.payment_status)}`}>
                  {getStatusIcon(invoice.payment_status)}
                  <span>
                    {invoice.payment_status === 'paid' && '已付款'}
                    {invoice.payment_status === 'cancelled' && '已取消'}
                    {invoice.payment_status === 'pending' && '待付款'}
                    {invoice.payment_status === 'scheduled' && '已排程'}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Items */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">發票明細</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    產品名稱
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    數量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    單價
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    小計
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    稅額
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoice.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{item.product_name}</div>
                      <div className="text-sm text-gray-500">{item.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.quantity.toLocaleString()} {item.unit}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">${item.unit_price.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">${item.line_total.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">${item.tax_amount.toLocaleString()}</div>
                      <div className="text-sm text-gray-500">{(item.tax_rate * 100).toFixed(1)}%</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Verification Details */}
        {invoice.verification_details && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">核對詳情</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">核對人員:</span>
                  <span className="text-gray-900 font-medium">
                    {invoice.verification_details.verified_by || '待核對'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">核對時間:</span>
                  <span className="text-gray-900 font-medium">
                    {invoice.verification_details.verified_at ? 
                      new Date(invoice.verification_details.verified_at).toLocaleString() : '待核對'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">採購單核對:</span>
                  <span className={`font-medium ${invoice.verification_details.po_match ? 'text-green-600' : 'text-red-600'}`}>
                    {invoice.verification_details.po_match ? '符合' : '不符合'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">收貨單核對:</span>
                  <span className={`font-medium ${invoice.verification_details.receiving_match ? 'text-green-600' : 'text-red-600'}`}>
                    {invoice.verification_details.receiving_match ? '符合' : '不符合'}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">價格核對:</span>
                  <span className={`font-medium ${invoice.verification_details.price_match ? 'text-green-600' : 'text-red-600'}`}>
                    {invoice.verification_details.price_match ? '符合' : '不符合'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">數量核對:</span>
                  <span className={`font-medium ${invoice.verification_details.quantity_match ? 'text-green-600' : 'text-red-600'}`}>
                    {invoice.verification_details.quantity_match ? '符合' : '不符合'}
                  </span>
                </div>
                {invoice.verification_details.quantity_variance !== 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">數量差異:</span>
                    <span className="text-gray-900 font-medium">{invoice.verification_details.quantity_variance}</span>
                  </div>
                )}
                {invoice.verification_details.variance_reason && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">差異原因:</span>
                    <span className="text-gray-900 font-medium">{invoice.verification_details.variance_reason}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment Details */}
        {invoice.payment_details && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">付款詳情</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">付款編號:</span>
                  <span className="text-gray-900 font-medium">{invoice.payment_details.payment_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">付款日期:</span>
                  <span className="text-gray-900 font-medium">
                    {new Date(invoice.payment_details.payment_date).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">付款方式:</span>
                  <span className="text-gray-900 font-medium">{invoice.payment_details.payment_method}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">付款金額:</span>
                  <span className="text-gray-900 font-medium">${invoice.payment_details.payment_amount.toLocaleString()}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">銀行帳號:</span>
                  <span className="text-gray-900 font-medium">{invoice.payment_details.bank_account}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">參考編號:</span>
                  <span className="text-gray-900 font-medium">{invoice.payment_details.reference_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">處理人員:</span>
                  <span className="text-gray-900 font-medium">{invoice.payment_details.processed_by}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Documents */}
        {invoice.documents && invoice.documents.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">相關文件</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {invoice.documents.map((doc) => (
                <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{doc.file_name}</div>
                      <div className="text-sm text-gray-500">
                        {doc.type === 'invoice' && '發票'}
                        {doc.type === 'payment_voucher' && '付款憑證'}
                        {doc.type === 'rejection_notice' && '拒付通知'}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(doc.uploaded_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">備註</h3>
            <p className="text-gray-700">{invoice.notes}</p>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderInvoiceList();
      case 'detail':
        return renderInvoiceDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="p-6">
      {renderWorkflowBanner()}
      {renderContent()}
    </div>
  );
};

export default InvoiceVerification; 